'use client';

import React from 'react';
import {useTracker} from '@udemy-v2/event-tracking';
import {useI18n} from '@udemy-v2/i18n';
import {BlockQuoteCardLink} from '@udemy-v2/react-product-card-components';

import {createTestimonialClickEvent, createUFBNoticeClickEvent} from '../../data/tracking';

interface TestimonialCardCtaProps {
    /** Stable tracking id for the testimonial group (TestimonialsGroup.trackingId). */
    id: string;
    /** Mapped event type (e.g. 'case-study'), from the CMS labelText. */
    type: string;
    url: string;
    /** CTA label — may contain HTML, matching the API content. */
    text: string;
    /** Whether this testimonial is a case study (fires an extra UFB notice event). */
    isCaseStudy: boolean;
}

/**
 * Per-card "read more" CTA for a testimonial, rendered with the design system's
 * `BlockQuoteCardLink` (label + trailing chevron). Client island so the click can
 * publish a `TestimonialClickEvent`, matching v1 (CVR-2382). Case-study testimonials
 * also publish a `UFBNoticeClickEvent`, mirroring v1's testimonials CMS wrapper.
 */
export const TestimonialCardCta: React.FC<TestimonialCardCtaProps> = ({
    id,
    type,
    url,
    text,
    isCaseStudy,
}) => {
    const tracker = useTracker();
    const {locale} = useI18n();

    return (
        <BlockQuoteCardLink
            renderLink={BlockQuoteCardLink.renderAnchorTag({
                href: url,
                onClick: () => {
                    tracker.publishEvent(
                        createTestimonialClickEvent({id, type, url, uiRegion: 'body'}),
                    );
                    if (isCaseStudy) {
                        tracker.publishEvent(
                            createUFBNoticeClickEvent({url, placement: 'body', locale}),
                        );
                    }
                },
            })}
        >
            <span dangerouslySetInnerHTML={{__html: text}} />
        </BlockQuoteCardLink>
    );
};
